/**
 * Enhanced Feedback Overlay Component
 * 
 * Provides rich visual feedback for correct/incorrect answers
 * with animations, sounds, and celebratory effects.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import ConfettiEffect from './ConfettiEffect';
import { 
  FEEDBACK_TYPES, 
  FEEDBACK_TIMEOUTS, 
  CONFETTI_CONFIG, 
  ENCOURAGING_MESSAGES,
  FEEDBACK_CONFIGS 
} from '../constants/feedback';

const FeedbackOverlay = ({ 
  feedback, 
  visible = false, 
  onClose = () => {},
  showExplanation = false,
  explanation = '',
  correctAnswer = '',
  score = 0,
  isComplete = false
}) => {
  const [animationPhase, setAnimationPhase] = useState('enter');
  const [showConfetti, setShowConfetti] = useState(false);

  const isCorrect = feedback === FEEDBACK_TYPES.CORRECT;
  const isIncorrect = feedback === FEEDBACK_TYPES.INCORRECT;

  // Add/remove body class to disable interactions behind overlay
  useEffect(() => {
    if (visible) {
      document.body.classList.add('overlay-open');
    } else {
      document.body.classList.remove('overlay-open');
    }
    return () => document.body.classList.remove('overlay-open');
  }, [visible]);

  // Calculate auto-close delay based on feedback type
  const autoCloseMs = useMemo(() => {
    if (isComplete) return FEEDBACK_TIMEOUTS.COMPLETE;
    if (isCorrect) return FEEDBACK_TIMEOUTS.CORRECT;
    return FEEDBACK_TIMEOUTS.INCORRECT;
  }, [isComplete, isCorrect]);

  // Extract close handler to avoid duplication
  // Use a ref to store onClose to prevent useEffect from re-running when it changes
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = useCallback(() => {
    setAnimationPhase('exit');
    setTimeout(() => {
      setShowConfetti(false);
      onCloseRef.current();
    }, FEEDBACK_TIMEOUTS.CLOSE_ANIMATION);
  }, []); // Empty deps - use ref instead

  // Store confetti timer ref to prevent multiple setups
  const confettiTimerRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  useEffect(() => {
    if (visible && feedback) {
      setAnimationPhase('enter');
      
      // Clear any existing timers
      if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      
      // Trigger confetti for correct answers or completion
      if (isCorrect || isComplete) {
        confettiTimerRef.current = setTimeout(() => {
          setShowConfetti(true);
          confettiTimerRef.current = null;
        }, FEEDBACK_TIMEOUTS.CONFETTI_DELAY);
      }

      // Auto-close after delay - longer for correct answers so kids can enjoy the celebration!
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
        autoCloseTimerRef.current = null;
      }, autoCloseMs);

      return () => {
        if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      };
    }
  }, [visible, feedback, isComplete, autoCloseMs, handleClose, isCorrect]);

  // Allow closing with Escape key when visible
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visible, handleClose]);

  // Memoize confetti completion callback to prevent re-render loops
  // MUST be called before any early returns to follow Rules of Hooks
  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  const getFeedbackConfig = () => {
    if (isComplete) {
      return FEEDBACK_CONFIGS.COMPLETE;
    } else if (isCorrect) {
      return FEEDBACK_CONFIGS.CORRECT;
    } else if (isIncorrect) {
      return FEEDBACK_CONFIGS.INCORRECT;
    }
    
    return FEEDBACK_CONFIGS.NEUTRAL;
  };

  if (!visible || !feedback) {
    return null;
  }

  const config = getFeedbackConfig();

  const getEncouragingMessages = () => {
    if (isCorrect) {
      const messages = ENCOURAGING_MESSAGES.CORRECT;
      return messages[Math.floor(Math.random() * messages.length)];
    } else if (isIncorrect) {
      const messages = ENCOURAGING_MESSAGES.INCORRECT;
      return messages[Math.floor(Math.random() * messages.length)];
    }
    return config.message;
  };

  return (
    <>
      {/* Confetti Effect - More particles for kids! */}
      <ConfettiEffect 
        active={showConfetti}
        particleCount={
          isComplete 
            ? CONFETTI_CONFIG.COMPLETE.PARTICLE_COUNT 
            : (isCorrect 
              ? CONFETTI_CONFIG.CORRECT.PARTICLE_COUNT 
              : CONFETTI_CONFIG.INCORRECT.PARTICLE_COUNT)
        }
        duration={autoCloseMs}
        onComplete={handleConfettiComplete}
      />

      {/* Feedback Overlay */}
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-300 bg-black/40 backdrop-blur-sm ${
          animationPhase === 'enter' ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        role="dialog"
        aria-live="polite"
        aria-modal="true"
        style={{ isolation: 'isolate', zIndex: 9999 }}
      >
        {/* Foreground Card - Compact */}
        <div 
          className={`relative w-[92%] max-w-xl text-center p-4 sm:p-6 rounded-2xl shadow-2xl ring-1 ring-white/20 bg-gradient-to-br ${config.bgColor} ${config.animation}`}
          style={{
            backgroundSize: '200% 200%',
            animation: `${animationPhase === 'enter' ? 'gradient-shift 2s ease-in-out infinite' : 'none'}, ${config.animation ? '' : ''}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            aria-label="Close"
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/25 hover:bg-white/35 active:bg-white/40 transition text-white text-xl leading-none flex items-center justify-center"
            onClick={handleClose}
          >
            ×
          </button>
          {/* Main Emoji - Compact */}
          <div className="text-6xl md:text-8xl mb-3 animate-bounce" style={{ 
            animation: 'bounce 0.8s ease-in-out infinite',
            transform: 'scale(1.1)'
          }}>
            {config.emoji}
          </div>

          {/* Title - Compact */}
          <h2 className={`text-3xl md:text-5xl font-extrabold ${config.textColor} mb-2`} style={{
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
            letterSpacing: '0.05em'
          }}>
            {config.title}
          </h2>

          {/* Encouraging Message - Compact */}
          <p className={`text-lg md:text-2xl ${config.textColor} mb-4 opacity-95 font-bold`} style={{
            textShadow: '1px 1px 3px rgba(0,0,0,0.15)'
          }}>
            {getEncouragingMessages()}
          </p>

          {/* Score Display for Completion */}
          {isComplete && (
            <div className={`text-xl md:text-2xl ${config.textColor} mb-3 font-semibold`}>
              Score: {Math.round(score)}%
            </div>
          )}

          {/* Explanation Section - Compact */}
          {showExplanation && (explanation || correctAnswer) && (
            <div className="mt-4 bg-white bg-opacity-20 rounded-xl p-3 backdrop-blur-sm">
              {correctAnswer && (
                <div className={`text-base md:text-lg ${config.textColor} mb-1 font-semibold`}>
                  Correct Answer: {correctAnswer}
                </div>
              )}
              {explanation && (
                <div className={`text-sm md:text-base ${config.textColor} opacity-90`}>
                  {explanation}
                </div>
              )}
            </div>
          )}

          {/* Decorative Elements */}
          <div className="absolute top-4 left-4 text-2xl opacity-70 star-twinkle">⭐</div>
          <div className="absolute top-8 right-6 text-xl opacity-70 star-twinkle" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute bottom-6 left-8 text-2xl opacity-70 star-twinkle" style={{ animationDelay: '1s' }}>🌟</div>
          <div className="absolute bottom-4 right-4 text-xl opacity-70 star-twinkle" style={{ animationDelay: '1.5s' }}>💫</div>
        </div>
      </div>

      {/* Additional CSS for gradient animation */}
      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </>
  );
};

FeedbackOverlay.propTypes = {
  feedback: PropTypes.oneOf([
    FEEDBACK_TYPES.CORRECT,
    FEEDBACK_TYPES.INCORRECT,
    FEEDBACK_TYPES.COMPLETE
  ]).isRequired,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  showExplanation: PropTypes.bool,
  explanation: PropTypes.string,
  correctAnswer: PropTypes.string,
  score: PropTypes.number,
  isComplete: PropTypes.bool
};

FeedbackOverlay.defaultProps = {
  visible: false,
  onClose: () => {},
  showExplanation: false,
  explanation: '',
  correctAnswer: '',
  score: 0,
  isComplete: false
};

export default memo(FeedbackOverlay);